<script>
  /**
   * @extends {"./ButtonSkeleton.svelte"} ButtonSkeletonProps
   * @restProps {button | a | div}
   * @slot {{ props: { role: "button"; type?: string; tabindex: any; disabled: boolean; href?: string; class: string; [key: string]: any; } }}
   */

  /**
   * Specify the kind of button
   * @type {"primary" | "secondary" | "tertiary" | "ghost" | "danger" | "danger-tertiary" | "danger-ghost" | "plus-minus" | "amount" }
   */
  export let kind = "primary";

  /**
   * Specify the size of button
   * @type {"default" | "field" | "small" | "lg" | "xl" }
   */
  export let size = "default";

  /** Specify the button label when no default slot is provided */
  export let text = "";

  /**
   * Set to `true` to use fullWith
   */
  export let large = false;

  /** Set to `true` to use Carbon's expressive typesetting */
  export let expressive = false;

  /**
   * Set to `true` to enable the selected state for an icon-only, ghost button
   */
  export let isSelected = false;

  /**
   * Specify the icon to render
   * Alternatively, use the named slot "icon" (e.g., `<Icon slot="icon" size="{20}" />`)
   * @type {any}
   */
  export let icon = undefined;

  /**
   * Specify the ARIA label for the button icon
   * @type {string}
   */
  export let iconDescription = undefined;

  /**
   * Set the alignment of the tooltip relative to the icon.
   * Only applies to icon-only buttons
   * @type {"start" | "center" | "end"}
   */
  export let tooltipAlignment = "center";

  /**
   * Set the position of the tooltip relative to the icon
   * @type {"top" | "right" | "bottom" | "left"}
   */
  export let tooltipPosition = "bottom";

  /**
   * Set to `true` to render a custom HTML element
   * Props are destructured as `props` in the default slot (e.g., <Button let:props><div {...props}>...</div></Button>)
   */
  export let as = false;

  /** Set to `true` to display the skeleton state */
  export let skeleton = false;

  /** Set to `true` to disable the button */
  export let disabled = false;

  /**
   * Set the `href` to use an anchor link
   * @type {string}
   */
  export let href = undefined;

  /** Specify the tabindex */
  export let tabindex = "0";

  /** Specify the `type` attribute for the button element */
  export let type = "button";

  /** Obtain a reference to the HTML element */
  export let ref = null;

  /** Set to `true` to put the icon on the left */
  export let iconLeft = false;

  /** Specify the number to display
   * @type {number}
   */
  export let value = null;

  /**
   * Specify the maximum value for the button
   * @type {number}
   */
  export let maxValue = null;

  /**
   * Specify the minimum value for the button
   * @type {number}
   */
  export let minValue = null;

  /** Specify the unit for the value */
  export let unit = "";

  /**
   * Set to `false` to hide the value next to the button
   * @default false
   */
  export let showValue = true;

  /**
   * Set to `true` to render a rounded button
   * @default false
   */
  export let rounded = false;

  /**
   * Set to `true` to render a small rounded button
   * @default false
   */
  export let smallRounded = false;

  /**
   * Specify the default value for the button.
   * @default 100
   */
  export let defaultValue = 100;

  /**
   * Set to `false` to not allow the number button to be editable
   * @default true
   */
  export let isEditable = true;

  /**
   * Set to `true` to show a loading state
   */
  export let loading = false;

  /**
   * Specify the notification number to display
   * @default 0
   */
  export let notificationNumber = 0;

  /**
   * Specify the total number of notifications to display
   * @default 0
   */
  export let totalNotification = 0;

  /**
   * Function to increment the button value
   * @default (i) => Math.ceil(Number(i) * 1.2)
   */
  export let increment = (i) => {
    if (i === 0) return 1;
    return Math.ceil(Number(i) * 1.2);
  };

  /**
   * Function to decrement the button value
   * @default (i) => Math.floor(Number(i) / 1.2)
   */
  export let decrement = (i) => {
    return Math.floor(Number(i) / 1.2);
  };

  /**
   * Function to reset the button value
   * @default () => {}
   */
  export let reset = () => {
    if (!disabled) {
      value = defaultValue;
    }
  };

  let isEditing = false;
  let isClicked = false;
  let svgContent = "";

  value = value !== null ? value : defaultValue;

  import { getContext } from "svelte";
  import ButtonSkeleton from "./ButtonSkeleton.svelte";
  import Tooltip from "../Tooltip/Tooltip.svelte";
  import Loading from "../Loading/Loading.svelte";

  const ctx = getContext("ComposedModal");

  let tooltipOpen = false;
  const tooltipId = "button-tooltip-" + Math.random().toString(36).slice(2);
  $: if (disabled || !hasIconOnly || !iconDescription) tooltipOpen = false;

  function tooltipTrigger(node) {
    const show = () => {
      if (!disabled && hasIconOnly && iconDescription) tooltipOpen = true;
    };
    const hide = () => {
      tooltipOpen = false;
    };
    const keydown = (event) => {
      if (event.key === "Escape" || event.key === "Tab") hide();
    };
    const events = {
      mouseenter: show,
      mouseleave: hide,
      focus: show,
      blur: hide,
      keydown,
    };
    for (const [name, handler] of Object.entries(events))
      node.addEventListener(name, handler);
    return {
      destroy() {
        for (const [name, handler] of Object.entries(events))
          node.removeEventListener(name, handler);
        hide();
      },
    };
  }

  function handleMinus() {
    if (!disabled) value = decrement(value);
  }

  function handlePlus() {
    if (!disabled) value = increment(value);
  }
  function handleDoubleClick() {
    if (!disabled) isEditing = true;
  }

  function handleBlur(event) {
    value = event.target.value;
    isEditing = false;
  }

  async function fetchSvg(url) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      svgContent = await res.text();
      if (!svgContent.includes("width=") && !svgContent.includes("height=")) {
        svgContent = svgContent.replace(/<svg/g, '<svg width="16" height="16"');
      }
    } catch (error) {
      console.error(`Error fetching SVG: ${error}`);
      svgContent = `<svg width="16" height="16"><text x="0" y="15" font-size="15">?</text></svg>`;
    }
  }

  let valueButtonRef = null;
  let numberPlusMinusButtonWidth;

  $: if (isEditing && valueButtonRef) {
    numberPlusMinusButtonWidth = valueButtonRef.getBoundingClientRect().width;
  }

  $: if (maxValue && value > maxValue) {
    value = maxValue;
  }

  $: if (minValue && value < minValue) {
    value = minValue;
  }

  $: if (icon && typeof icon === "string") {
    fetchSvg(icon);
  }

  $: if (ctx && ref) {
    ctx.declareRef(ref);
  }
  $: hasIconOnly = (icon || $$slots.icon) && !$$slots.default && !text;
  $: hasIcon = (icon || $$slots.icon) && ($$slots.default || text);
  $: iconProps = {
    "aria-hidden": "true",
    class: "bx--btn__icon",
    "aria-label": iconDescription,
  };
  $: buttonProps = {
    type: href && !disabled ? undefined : type,
    tabindex,
    disabled: disabled === true ? true : undefined,
    href,
    "aria-pressed":
      hasIconOnly && kind === "ghost" && !href ? isSelected : undefined,
    ...$$restProps,
    "aria-describedby": tooltipOpen
      ? [$$restProps["aria-describedby"], tooltipId].filter(Boolean).join(" ")
      : $$restProps["aria-describedby"],
    class: [
      "bx--btn",
      expressive && "bx--btn--expressive",
      ((size === "small" && !expressive) ||
        (size === "sm" && !expressive) ||
        (size === "small" && !expressive)) &&
        "bx--btn--sm",
      (size === "field" && !expressive) ||
        (size === "md" && !expressive && "bx--btn--md"),
      size === "field" && "bx--btn--field",
      size === "small" && "bx--btn--sm",
      size === "lg" && "bx--btn--lg",
      size === "xl" && "bx--btn--xl",
      kind && `bx--btn--${kind}`,
      disabled && "bx--btn--disabled",
      hasIconOnly && "bx--btn--icon-only",
      hasIconOnly &&
        tooltipPosition &&
        iconDescription &&
        `bx--btn--icon-only--${tooltipPosition}`,
      hasIconOnly &&
        tooltipAlignment &&
        iconDescription &&
        `bx--tooltip--align-${tooltipAlignment}`,
      hasIconOnly && isSelected && kind === "ghost" && "bx--btn--selected",
      $$restProps.class,
      hasIcon && "bx--btn--icon",
      iconLeft && "bx--btn--icon-left",
      rounded && "bx--btn--rounded",
      smallRounded && "bx--btn--small-rounded",
      large && "bx--btn--large",
    ]
      .filter(Boolean)
      .join(" "),
  };
</script>

<!-- svelte-ignore a11y-mouse-events-have-key-events -->
{#if skeleton}
  <ButtonSkeleton
    href="{href}"
    size="{size}"
    {...$$restProps}
    style="{hasIconOnly && 'width: 3rem;'}"
    on:click
    on:focus
    on:blur
    on:mouseover
    on:mouseenter
    on:mouseleave
  />
{:else if loading}
  <div
    class="bx--btn bx--btn--{kind} bx--btn--loading bx--btn--disabled"
    class:bx--btn--icon-only="{hasIconOnly}"
    class:bx--btn--field="{size === 'field'}"
    class:bx--btn--sm="{size === 'small'}"
    class:bx--btn--lg="{size === 'lg'}"
    class:bx--btn--xl="{size === 'xl'}"
    style:min-width="{hasIconOnly ? '3rem' : undefined}"
  >
    <Loading withOverlay="{false}" small />
  </div>
{:else if kind === "plus-minus"}
  <div
    class:bx--btn--plus-minus="{true}"
    class:bx--btn--plus-minus__disable="{disabled}"
  >
    <button
      on:click="{handleMinus}"
      class:bx--btn--plus-minus__minus="{true}"
      class:bx--btn--plus-minus__disable="{disabled ||
        (minValue && value <= minValue)}"
      disabled="{minValue && value <= minValue}"
    >
      <img src="/img/assistant/icons/minus.svg" alt="less" />
    </button>
    <div
      class:bx--btn--plus-minus__center="{true}"
      class:bx--btn--plus-minus__disable="{disabled}"
    >
      <div class="text"><slot>{text}</slot></div>
      <button
        bind:this="{valueButtonRef}"
        class:bx--btn--plus-minus__value="{true}"
        class:bold="{true}"
        class:value="{true}"
        class:bx--btn--plus-minus__hideValue="{!showValue}"
        class:bx--btn--plus-minus__noEditable="{!isEditable}"
        class:bx--btn--plus-minus__disable="{disabled}"
        class:bx--btn--plus-minus__is-editing="{isEditing && isEditable}"
        on:click="{reset}"
        class:disable="{disabled}"
        on:dblclick="{handleDoubleClick}"
      >
        {#if isEditing && isEditable}
          <input
            type="number"
            class="no-steppers"
            style="width:{numberPlusMinusButtonWidth}px !important;"
            bind:value="{value}"
            on:blur="{handleBlur}"
            on:keypress="{(event) => {
              if (
                event.key === 'Enter' ||
                event.key === 'Escape' ||
                event.key === 'Tab' ||
                event.key === ' '
              ) {
                event.preventDefault();
                isEditing = false;
              }
            }}"
          />
        {:else if showValue}
          {#if value !== null}{value.toString()}{/if}
          {#if unit !== ""}{unit}{/if}
        {/if}
      </button>
    </div>
    <button
      on:click="{handlePlus}"
      class:bx--btn--plus-minus__plus="{true}"
      class:bx--btn--plus-minus__disable="{disabled ||
        (maxValue && value >= maxValue)}"
      class:disable="{disabled}"
      disabled="{maxValue && value >= maxValue}"
    >
      <img src="/img/assistant/icons/plus.svg" alt="more" />
    </button>
  </div>
{:else if kind === "amount"}
  <div class:bx--btn--amount="{true}" class:bx--btn--disabled="{disabled}">
    <button
      class:clicked="{isClicked}"
      disabled="{disabled}"
      bind:this="{ref}"
      {...buttonProps}
      on:click
      on:focus
      on:blur
      on:mouseover
      on:mouseenter
      on:mouseleave
      on:mousedown="{() => {
        isClicked = true;
      }}"
      on:mouseup="{() => {
        isClicked = false;
      }}"
      on:mouseleave="{() => {
        isClicked = false;
      }}"><slot>{text}</slot></button
    >
    <input
      type="number"
      disabled="{!isEditable || disabled}"
      class:bx--btn--amount__input="{true}"
      class:bx--btn--amount--noEditable="{!isEditable}"
      bind:value="{value}"
      on:blur="{handleBlur}"
    />
  </div>
{:else if as}
  <slot props="{buttonProps}">{text}</slot>
{:else if href && !disabled}
  <!-- svelte-ignore a11y-missing-attribute -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <a
    use:tooltipTrigger
    bind:this="{ref}"
    {...buttonProps}
    on:click
    on:focus
    on:blur
    on:mouseover
    on:mouseenter
    on:mouseleave
    class:bx--btn--notification="{notificationNumber || totalNotification}"
  >
    {#if hasIconOnly && iconDescription}
      <span class:bx--assistive-text="{true}">{iconDescription}</span>
    {/if}
    <slot>{text}</slot>
    {#if $$slots.icon}
      <slot
        name="icon"
        style="{hasIconOnly ? 'margin-left: 0' : undefined}"
        {...iconProps}
      />
    {:else if icon}
      {#if typeof icon === "function"}
        <svelte:component
          this="{icon}"
          style="{hasIconOnly ? 'margin-left: 0' : undefined}"
          {...iconProps}
        />
      {:else}
        {@html svgContent}
      {/if}
    {/if}
  </a>
  {#if notificationNumber || totalNotification}
    <div class="bx--btn--notification-number">
      {totalNotification > 0 && notificationNumber !== totalNotification
        ? notificationNumber + " of " + totalNotification
        : notificationNumber || 0}
    </div>
  {/if}
{:else}
  <button
    use:tooltipTrigger
    bind:this="{ref}"
    {...buttonProps}
    on:click
    on:focus
    on:blur
    on:mouseover
    on:mouseenter
    on:mouseleave
    class:bx--btn--notification="{notificationNumber || totalNotification}"
  >
    {#if hasIconOnly && iconDescription}
      <span class:bx--assistive-text="{true}">{iconDescription}</span>
    {/if}
    <slot>{text}</slot>
    {#if $$slots.icon}
      <slot
        name="icon"
        style="{hasIconOnly ? 'margin-left: 0' : undefined}"
        {...iconProps}
      />
    {:else if icon}
      {#if typeof icon === "function"}
        <svelte:component
          this="{icon}"
          style="{hasIconOnly ? 'margin-left: 0' : undefined}"
          {...iconProps}
        />
      {:else}
        {@html svgContent}
      {/if}
    {/if}
    {#if notificationNumber || totalNotification}
      <div class="bx--btn--notification-number">
        {totalNotification > 0 && notificationNumber !== totalNotification
          ? notificationNumber + " of " + totalNotification
          : notificationNumber || 0}
      </div>
    {/if}
  </button>
{/if}

{#if hasIconOnly && iconDescription && ref && !disabled && !skeleton && !loading && !as}
  <Tooltip
    triggerRef="{ref}"
    hideIcon
    text="{iconDescription}"
    direction="{tooltipPosition}"
    align="{tooltipAlignment}"
    tooltipId="{tooltipId}"
    bind:open="{tooltipOpen}"
  />
{/if}
